import { DEFAULT_ICONS_SVG, EMAIL_TEMPLATES, DESIGN, ADMIN_EMAIL_CONTENT } from '@/constants/emails/emails.constants';
import type { TemplateOverride } from '@monolog/shared/types/database/communications-svc';

/**
 * formValues keys that are store-level (one logo + one footer per store), not per-template. Editing
 * any of these on one template propagates to all templates: shared live in the editor (store deep-watch)
 * and persisted to every template override on save (see useEmailDesigner.saveTemplateOverride).
 */
export const STORE_LEVEL_EMAIL_KEYS = [
  'logoUrl',
  'logo',
  'desktopLogoWidth',
  'mobileLogoWidth',
  'footerContent',
  'togglePoweredByOpEnabled',
] as const;

const VOID_ELEMENTS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
]);

/**
 * Lightweight HTML formatter that adds newlines and indentation.
 * Skips formatting if the HTML already contains newlines (already formatted).
 */
export function formatHtml(html: string): string {
  if (!html || typeof html !== 'string') return html;
  if (html.includes('\n') && html.trim().split('\n').length > 2) return html;
  const containsScriptOrStyleTags = /<(script|style)[^>]*>.+<\/(script|style)>/is;
  if (containsScriptOrStyleTags.test(html)) return html;

  let formatted = '';
  let indent = 0;
  const tab = '  ';

  // Split on tags while keeping them
  const tokens = html.replace(/>\s*</g, '>\n<').split('\n');

  for (const token of tokens) {
    const trimmed = token.trim();
    if (!trimmed) continue;

    const isClosing = /^<\//.test(trimmed);
    const isOpening = /^<[a-zA-Z]/.test(trimmed);
    const isSelfClosing = /\/>$/.test(trimmed);
    const tagMatch = trimmed.match(/^<\/?([a-zA-Z][a-zA-Z0-9]*)/);
    const tagName = tagMatch ? tagMatch[1].toLowerCase() : '';
    const isVoid = VOID_ELEMENTS.has(tagName);

    if (isClosing) indent = Math.max(0, indent - 1);

    formatted += tab.repeat(indent) + trimmed + '\n';

    if (isOpening && !isSelfClosing && !isVoid && !isClosing) indent++;
  }

  return formatted.trimEnd();
}

/**
 * Helper function to set advancedConfig from template override content
 * when customizationValues is empty
 */
export function setAdvancedConfigFromContent(
  formValues: ReturnType<typeof createInitialFormValues>,
  override: TemplateOverride,
): void {
  const hasCustomizationValues =
    override.customizationValues &&
    typeof override.customizationValues === 'object' &&
    Object.keys(override.customizationValues).length > 0;
  if (
    !hasCustomizationValues &&
    override.content &&
    typeof override.content === 'string' &&
    override.content.trim().length > 0
  ) {
    formValues.advancedConfig = formatHtml(override.content);
  }
}

// Function to create initial form values with optional customizations
export function createInitialFormValues(
  customizations?: any,
  templateKey?: string,
  availableTemplates?: any,
  storeName?: string,
) {
  const currentTemplate = availableTemplates?.[templateKey || 'approved'] || availableTemplates?.approved;

  return {
    adminEmailContent: customizations?.adminEmailContent || { ...ADMIN_EMAIL_CONTENT },
    advancedConfig: formatHtml(customizations?.advancedConfig) || '',
    buttonBackground: customizations?.buttonBackground || DESIGN.colors.text.primary,
    buttonBorderRadius: customizations?.buttonBorderRadius || DESIGN.spacing.buttonBorderRadius,
    buttonFontColor: customizations?.buttonFontColor || DESIGN.colors.text.white,
    buttonFontStyle: customizations?.buttonFontStyle || ('none' as 'none' | 'uppercase' | 'capitalize'),
    buttonLink: customizations?.buttonLink || currentTemplate?.buttonLink || '',
    buttonText: customizations?.buttonText || currentTemplate?.buttonText || '',
    coverageTermsUrl: customizations?.coverageTermsUrl || '',
    desktopLogoWidth: parseInt(
      customizations?.desktopLogoWidth?.toString() || DESIGN.spacing.desktopLogoWidth.toString(),
      10,
    ),
    footerContent: customizations?.footerContent || `${storeName}, All Rights Reserved`,
    headline: customizations?.headline || currentTemplate?.headline || '',
    headlineDesktopFontColor: customizations?.headlineDesktopFontColor || DESIGN.colors.text.primary,
    headlineDesktopFontFamily: customizations?.headlineDesktopFontFamily || DESIGN.fonts.family.primary,
    headlineDesktopFontSize: customizations?.headlineDesktopFontSize || DESIGN.typography.headline.desktop.fontSize,
    headlineDesktopFontWeight: customizations?.headlineDesktopFontWeight || ('bold' as 'bold' | 'regular'),
    headlineDesktopLetterSpacing: customizations?.headlineDesktopLetterSpacing || 0,
    headlineDesktopLineHeight:
      customizations?.headlineDesktopLineHeight || DESIGN.typography.headline.desktop.lineHeight,
    headlineFontStyle: customizations?.headlineFontStyle || ('none' as 'none' | 'uppercase' | 'capitalize'),
    headlineMobileFontColor: customizations?.headlineMobileFontColor || DESIGN.colors.text.primary,
    headlineMobileFontFamily: customizations?.headlineMobileFontFamily || DESIGN.fonts.family.primary,
    headlineMobileFontSize: customizations?.headlineMobileFontSize || DESIGN.typography.headline.mobile.fontSize,
    headlineMobileFontWeight: customizations?.headlineMobileFontWeight || ('bold' as 'bold' | 'regular'),
    headlineMobileLetterSpacing: customizations?.headlineMobileLetterSpacing || 0,
    headlineMobileLineHeight: customizations?.headlineMobileLineHeight || DESIGN.typography.headline.mobile.lineHeight,
    iconUrl: customizations?.iconUrl || iconUrl(templateKey),
    logo: customizations?.logo || '',
    logoUrl: customizations?.logoUrl || '',
    marginBottom: customizations?.marginBottom || 0,
    marginLeft: customizations?.marginLeft || 0,
    marginRight: customizations?.marginRight || 0,
    marginTop: customizations?.marginTop || 0,

    mobileLogoWidth: parseInt(
      customizations?.mobileLogoWidth?.toString() || DESIGN.spacing.desktopLogoWidth.toString(),
      10,
    ),

    paddingBottom: customizations?.paddingBottom || 0,
    // Padding and margin fields
    paddingLeft: customizations?.paddingLeft || 0,
    paddingRight: customizations?.paddingRight || 0,
    paddingTop: customizations?.paddingTop || 0,
    paragraph: customizations?.paragraph || currentTemplate?.mainText || '',
    paragraphDesktopFontColor: customizations?.paragraphDesktopFontColor || DESIGN.colors.text.primary,
    paragraphDesktopFontFamily: customizations?.paragraphDesktopFontFamily || DESIGN.fonts.family.primary,
    paragraphDesktopFontSize: customizations?.paragraphDesktopFontSize || DESIGN.typography.paragraph.desktop.fontSize,
    paragraphDesktopFontWeight: customizations?.paragraphDesktopFontWeight || ('regular' as 'bold' | 'regular'),
    paragraphDesktopLetterSpacing: customizations?.paragraphDesktopLetterSpacing || 0,
    paragraphDesktopLineHeight:
      customizations?.paragraphDesktopLineHeight || DESIGN.typography.paragraph.desktop.lineHeight,
    paragraphFontStyle: customizations?.paragraphFontStyle || ('none' as 'none' | 'uppercase' | 'capitalize'),
    paragraphMobileFontColor: customizations?.paragraphMobileFontColor || DESIGN.colors.text.primary,
    paragraphMobileFontFamily: customizations?.paragraphMobileFontFamily || DESIGN.fonts.family.primary,
    paragraphMobileFontSize: customizations?.paragraphMobileFontSize || DESIGN.typography.paragraph.mobile.fontSize,
    paragraphMobileFontWeight: customizations?.paragraphMobileFontWeight || ('regular' as 'bold' | 'regular'),
    paragraphMobileLetterSpacing: customizations?.paragraphMobileLetterSpacing || 0,
    paragraphMobileLineHeight:
      customizations?.paragraphMobileLineHeight || DESIGN.typography.paragraph.mobile.lineHeight,
    storeName: customizations?.storeName || storeName,
    subText:
      customizations?.subText ||
      (currentTemplate?.subText && typeof currentTemplate.subText === 'function'
        ? currentTemplate.subText(storeName)
        : currentTemplate?.subText || ''),
    subTextDesktopFontColor: customizations?.subTextDesktopFontColor || DESIGN.colors.text.primary,
    subTextDesktopFontFamily: customizations?.subTextDesktopFontFamily || DESIGN.fonts.family.primary,
    subTextDesktopFontSize: customizations?.subTextDesktopFontSize || 20,
    subTextDesktopFontStyle: customizations?.subTextDesktopFontStyle || ('none' as 'none' | 'uppercase' | 'capitalize'),
    subTextDesktopFontWeight: customizations?.subTextDesktopFontWeight || ('regular' as 'bold' | 'regular'),
    subTextDesktopLetterSpacing: customizations?.subTextDesktopLetterSpacing || 0,
    subTextDesktopLineHeight: customizations?.subTextDesktopLineHeight || 1.5,
    subTextMobileFontSize: customizations?.subTextMobileFontSize || DESIGN.typography.paragraph.mobile.fontSize,
    subTextMobileLetterSpacing: customizations?.subTextMobileLetterSpacing || 0,
    subTextMobileLineHeight: customizations?.subTextMobileLineHeight || DESIGN.typography.paragraph.mobile.lineHeight,
    togglePoweredByOpEnabled: customizations?.togglePoweredByOpEnabled ?? true,
    // Visual email builder state — only included when present, so absent builder data doesn't add
    // undefined keys (which would make hasUnsavedChanges spuriously dirty against the JSON baseline).
    ...(customizations?.emailBlocks ? { emailBlocks: customizations.emailBlocks } : {}),
    ...(customizations?.__builderMode ? { __builderMode: customizations.__builderMode } : {}),
  };
}

export function iconUrl(templateKey?: string) {
  const key = templateKey || 'approved';

  return DEFAULT_ICONS_SVG[key] || '';
}

export function getAvailableTemplates(merchantStore: any) {
  // Handle null merchant store (SSR case)
  if (!merchantStore) {
    console.error('getAvailableTemplates: merchantStore is null, returning default templates');

    return {
      approved: EMAIL_TEMPLATES.approved,
    };
  }

  const templates: any = {
    approved: EMAIL_TEMPLATES.approved,
    denied: EMAIL_TEMPLATES.denied,
    inactive: EMAIL_TEMPLATES.inactive,
    received: EMAIL_TEMPLATES.received,
    unread: EMAIL_TEMPLATES.unread,
  };

  // Only add orderProtected template if sendConfirmationEmail is enabled
  if (merchantStore.currentStore?.generalSettings?.sendConfirmationEmail) {
    templates.orderProtected = EMAIL_TEMPLATES.orderProtected;
  }

  // Only add storeCredit template if capability is enabled
  if (merchantStore.currentStore?.capabilities?.credit) {
    templates.storeCredit = EMAIL_TEMPLATES.storeCredit;
  }

  // Only add extendedWarranty template if capability is enabled
  if (merchantStore.currentStore?.capabilities?.extendedWarranties) {
    templates.extendedWarranty = EMAIL_TEMPLATES.extendedWarranty;
  }

  return templates;
}

/** Claim templates whose replies route by the order number in the subject, with the variable each one sends. */
const CLAIM_TEMPLATE_ORDER_VARIABLE: Partial<Record<string, string>> = {
  customer_claim_approved: 'storeOrderId',
  customer_claim_denied: 'storeOrderId',
  customer_close_inactive_claim: 'storeOrderId',
  customer_new_claim: 'storeOrderId',
  customer_new_message: 'storeOrderId',
};

/**
 * The saved subject for a template: its headline, plus the order number for claim templates whose headline
 * has none. Inbound mail falls back to the order number in the subject to match a customer's reply to a
 * claim, so a subject like "You have an unread message." left replies unroutable.
 */
export function subjectWithOrderNumber(headline: string, templateName: string): string {
  const orderVariable = CLAIM_TEMPLATE_ORDER_VARIABLE[templateName];
  // Only the variable the template actually sends counts; any other order variable renders empty.
  if (!orderVariable || !headline || headline.includes(`[[${orderVariable}]]`)) return headline;

  return `${headline.trim()} - Order [[${orderVariable}]]`;
}
